import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import MembersList from './components/MembersList';
import MemberForm from './components/MemberForm';
import MemberDetails from './components/MemberDetails';
import Reports from './components/Reports';
import PricingSettings from './components/PricingSettings';
import Navbar from './components/Navbar';
import MemberLogin from './components/member/MemberLogin';
import MemberSignup from './components/member/MemberSignup';
import MemberDashboard from './components/member/MemberDashboard';
import MemberNavbar from './components/member/MemberNavbar';
import Chatbot from './components/member/Chatbot';
import AdminLayout from './components/AdminLayout';
import { authAPI } from './services/apiService';
import { canAccessSetup, normalizeStaffUser } from './utils/roles';

function App() {
  const [user, setUser] = useState(null);
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Paint immediately from stored session — don't block UI on network verify
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('user');
    const memberToken = localStorage.getItem('memberToken');
    const memberData = localStorage.getItem('member');

    if (token && userData) {
      try {
        const normalized = normalizeStaffUser(JSON.parse(userData));
        setUser(normalized);
        localStorage.setItem('user', JSON.stringify(normalized));
      } catch (_) { /* ignore */ }
    } else if (memberToken && memberData && !String(memberToken).startsWith('{')) {
      try {
        setMember(JSON.parse(memberData));
      } catch (_) { /* ignore */ }
    } else if (memberToken && String(memberToken).startsWith('{')) {
      localStorage.removeItem('memberToken');
      localStorage.removeItem('member');
    }
    setLoading(false);

    const checkAuth = async () => {
      if (token && userData) {
        try {
          const verified = await authAPI.verify();
          const stored = JSON.parse(userData);
          const merged = normalizeStaffUser({
            ...stored,
            id: stored.id || verified.user?.userId,
            username: stored.username || verified.user?.username,
            role: verified.user?.role || stored.role || 'admin'
          });
          setUser(merged);
          localStorage.setItem('user', JSON.stringify(merged));
        } catch (error) {
          if (error?.response?.status === 401) {
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            setUser(null);
          }
        }
      } else if (memberToken && !String(memberToken).startsWith('{')) {
        try {
          const session = await authAPI.memberMe();
          if (session?.member) {
            setMember(session.member);
            localStorage.setItem('member', JSON.stringify(session.member));
          }
        } catch (error) {
          if (error?.response?.status === 401 || error?.response?.status === 403) {
            localStorage.removeItem('memberToken');
            localStorage.removeItem('member');
            setMember(null);
          }
        }
      }
    };

    checkAuth();
  }, []);

  const handleLogin = (userData) => {
    const normalized = normalizeStaffUser(userData);
    setUser(normalized);
    localStorage.setItem('user', JSON.stringify(normalized));
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setUser(null);
  };

  const handleMemberLogin = (memberData) => {
    setMember(memberData);
    localStorage.setItem('member', JSON.stringify(memberData));
  };

  const handleMemberLogout = () => {
    localStorage.removeItem('memberToken');
    localStorage.removeItem('member');
    setMember(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <div className="min-h-screen bg-slate-100">
        <Routes>
          {/* Admin Portal - Default landing page */}
          <Route path="/" element={
            user ? (
              <Navigate to="/adminportal/dashboard" replace />
            ) : (
              <>
                <Login onLogin={handleLogin} />
                <Toaster position="top-right" />
              </>
            )
          } />

          {/* Member Portal Routes */}
          <Route path="/member" element={
            member ? (
              <Navigate to="/member/dashboard" replace />
            ) : (
              <>
                <MemberLogin onLogin={handleMemberLogin} />
                <Toaster position="top-right" />
              </>
            )
          } />
          
          <Route path="/signup" element={
            member ? (
              <Navigate to="/member/dashboard" replace />
            ) : (
              <>
                <MemberSignup />
                <Toaster position="top-right" />
              </>
            )
          } />
          
          <Route path="/member/dashboard" element={
            member ? (
              <div className="min-h-screen bg-slate-100">
                <MemberNavbar member={member} onLogout={handleMemberLogout} onPasswordChanged={handleMemberLogin} />
                <main className="container mx-auto px-4 py-6 sm:py-8">
                  <MemberDashboard member={member} />
                </main>
                <Toaster position="top-right" />
              </div>
            ) : (
              <Navigate to="/member" replace />
            )
          } />
          
          <Route path="/member/chatbot" element={
            member ? (
              <div className="min-h-screen bg-slate-100">
                <MemberNavbar member={member} onLogout={handleMemberLogout} onPasswordChanged={handleMemberLogin} />
                <main className="container mx-auto px-4 py-4 sm:py-6">
                  <Chatbot member={member} />
                </main>
                <Toaster position="top-right" />
              </div>
            ) : (
              <Navigate to="/member" replace />
            )
          } />
          
          <Route path="/dashboard" element={
            user ? (
              <Navigate to="/adminportal/dashboard" replace />
            ) : member ? (
              <Navigate to="/member/dashboard" replace />
            ) : (
              <Navigate to="/" replace />
            )
          } />

          {/* Admin Portal Routes */}
          <Route path="/adminportal" element={
            user ? (
              <AdminLayout user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/" replace />
            )
          }>
            <Route index element={<Navigate to="/adminportal/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="members" element={<MembersList />} />
            <Route path="members/new" element={<MemberForm />} />
            <Route path="members/:id" element={<MemberDetails />} />
            <Route path="members/:id/edit" element={<MemberForm />} />
            <Route path="reports" element={<Reports />} />
            <Route
              path="setup"
              element={
                canAccessSetup(user)
                  ? <PricingSettings />
                  : <Navigate to="/adminportal/dashboard" replace />
              }
            />
            <Route path="settings/pricing" element={<Navigate to="/adminportal/setup" replace />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" />
      </div>
    </Router>
  );
}

export default App;

